import { ComponentType } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { cva, VariantProps } from "class-variance-authority";

export const modalSizes = cva(
  "relative w-full max-h-[90vh] space-y-4 rounded-xl bg-white duration-75 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 overflow-y-auto overflow-x-hidden",
  {
    variants: {
      size: {
        sm: "max-w-xl",
        md: "max-w-2xl",
        lg: "max-w-3xl",
        xl: "max-w-7xl",
      },
      padding: {
        none: "",
        sm: "p-7",
        md: "p-8",
        lg: "p-10",
      },
    },
    defaultVariants: {
      size: "sm",
      padding: "sm",
    },
  },
);

export type ModalCloseProps = {
  onClose: (value: boolean) => void;
  preventClose?: boolean;
};

type ModalProps<T> = VariantProps<typeof modalSizes> &
  ModalCloseProps & {
    isOpen: boolean;
    content: ComponentType<T & ModalCloseProps>;
    contentProps?: T;
  };

const Modal = <T,>({
  isOpen = false,
  onClose,
  content: ContentComponent,
  contentProps = {} as T,
  preventClose = false,
  size,
  padding,
}: ModalProps<T>) => {
  return (
    <Dialog className="relative z-50" open={isOpen} onClose={(value) => !preventClose && onClose(value)}>
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-overlay backdrop-blur-sm duration-75 ease-out data-[closed]:opacity-0"
      />

      <div className="fixed inset-0 flex w-screen items-center justify-center px-10">
        <DialogPanel transition className={modalSizes({ size, padding })}>
          <ContentComponent onClose={onClose} preventClose={preventClose} {...contentProps} />
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default Modal;
